<script setup lang="ts">
import { ClipboardCheck, Home, RadioTower, ReceiptText, UserRound } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useMobileStore } from '@/stores/mobile'

const route = useRoute()
const router = useRouter()
const store = useMobileStore()
const online = ref(navigator.onLine)
const hideNav = computed(() => Boolean(route.meta.hideNav))
const activeTab = computed(() => route.meta.tab)

// 原生手感:Tab 间即时切换;进入/退出沉浸页(详情/开单/质检/消息)用轻推入、弹出。
// 每个 Tab 记住自己的滚动位置,沉浸页总是从顶部开始 —— 滚动容器是设备 viewport,不是 window。
const viewport = ref<HTMLElement | null>(null)
const transitionName = ref('mobile-none')
const tabScrollPositions = new Map<string, number>()
let pendingScrollTop = 0
const removeNavGuard = router.beforeEach((to, from) => {
  if (from.meta.tab && !from.meta.hideNav) tabScrollPositions.set(String(from.meta.tab), viewport.value?.scrollTop ?? 0)
  const toImmersive = Boolean(to.meta.hideNav)
  const fromImmersive = Boolean(from.meta.hideNav)
  if (toImmersive && !fromImmersive) transitionName.value = 'mobile-push'
  else if (!toImmersive && fromImmersive) transitionName.value = 'mobile-pop'
  else transitionName.value = 'mobile-none'
  pendingScrollTop = !toImmersive && to.meta.tab ? tabScrollPositions.get(String(to.meta.tab)) ?? 0 : 0
})
// enter 钩子(单参:不接管 CSS 过渡时序)——此刻新页面已插入,scrollHeight 正确,可恢复位置
const restoreScroll = (_el: Element) => {
  if (viewport.value) viewport.value.scrollTop = pendingScrollTop
  scrolled.value = pendingScrollTop > 4
}

// ===== iOS 手感:滚动阴影 / 边缘右滑返回 / 下拉刷新 =====
const scrolled = ref(false)
const refreshing = ref(false)
const pullDistance = ref(0)
const shellToast = ref('')
let shellToastTimer: number | undefined
const showShellToast = (message: string) => {
  shellToast.value = message
  window.clearTimeout(shellToastTimer)
  shellToastTimer = window.setTimeout(() => { shellToast.value = '' }, 2200)
}

const pageEl = () => viewport.value?.querySelector<HTMLElement>('.mobile-page') ?? null
const onScroll = () => { scrolled.value = (viewport.value?.scrollTop ?? 0) > 4 }

type GestureMode = 'none' | 'pending' | 'back' | 'pull'
let gesture: GestureMode = 'none'
let startX = 0
let startY = 0
let startTime = 0
let backDx = 0
const BACK_EDGE = 30
const PULL_THRESHOLD = 58

const onTouchStart = (event: TouchEvent) => {
  if (event.touches.length !== 1 || refreshing.value) { gesture = 'none'; return }
  const touch = event.touches[0]!
  startX = touch.clientX
  startY = touch.clientY
  startTime = performance.now()
  backDx = 0
  gesture = 'pending'
}

const onTouchMove = (event: TouchEvent) => {
  if (gesture === 'none') return
  const touch = event.touches[0]!
  const dx = touch.clientX - startX
  const dy = touch.clientY - startY
  if (gesture === 'pending') {
    if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
    const horizontal = Math.abs(dx) > Math.abs(dy)
    if (horizontal && dx > 0 && startX <= BACK_EDGE && hideNav.value) gesture = 'back'
    else if (!horizontal && dy > 0 && !hideNav.value && (viewport.value?.scrollTop ?? 0) <= 0) gesture = 'pull'
    else { gesture = 'none'; return }
  }
  if (gesture === 'back') {
    event.preventDefault()
    backDx = Math.max(0, dx)
    const el = pageEl()
    if (el) {
      el.style.transition = 'none'
      el.style.transform = `translateX(${backDx}px)`
    }
  } else if (gesture === 'pull') {
    if (dy <= 0) { pullDistance.value = 0; gesture = 'none'; return }
    event.preventDefault()
    pullDistance.value = Math.min(96, dy * 0.42)
  }
}

const onTouchEnd = () => {
  if (gesture === 'back') {
    const el = pageEl()
    const velocity = backDx / Math.max(1, performance.now() - startTime)
    const commit = backDx > 110 || (backDx > 36 && velocity > 0.45)
    if (el) {
      el.style.transition = 'transform 160ms ease-out'
      if (commit) {
        el.style.transform = 'translateX(100%)'
        window.setTimeout(() => router.back(), 120)
      } else {
        el.style.transform = ''
        window.setTimeout(() => { el.style.transition = '' }, 200)
      }
    } else if (commit) {
      router.back()
    }
  } else if (gesture === 'pull') {
    if (pullDistance.value >= PULL_THRESHOLD) void runRefresh()
    else pullDistance.value = 0
  }
  gesture = 'none'
}

const runRefresh = async () => {
  refreshing.value = true
  pullDistance.value = PULL_THRESHOLD
  const startedAt = performance.now()
  try { await store.initialize() } catch { /* lastError 已由 store 记录 */ }
  await new Promise((resolve) => setTimeout(resolve, Math.max(0, 700 - (performance.now() - startedAt))))
  refreshing.value = false
  pullDistance.value = 0
  showShellToast('本地演示数据已同步')
}
// 桌面演示框内的模拟状态栏;真机(<900px)由系统状态栏接管,不渲染
const clock = ref('')
const statusLight = computed(() => route.name === 'mobile-home')
let clockTimer: number | undefined
const syncClock = () => {
  clock.value = new Intl.DateTimeFormat('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date())
}

const navItems: Array<{ label: string; path: string; tab: 'home' | 'tasks' | 'scan' | 'orders' | 'profile'; icon: Component; primary?: boolean }> = [
  { label: '首页', path: '/home', tab: 'home', icon: Home },
  { label: '任务', path: '/tasks', tab: 'tasks', icon: ClipboardCheck },
  { label: '扫码', path: '/scan', tab: 'scan', icon: RadioTower, primary: true },
  { label: '订单', path: '/orders', tab: 'orders', icon: ReceiptText },
  { label: '我的', path: '/profile', tab: 'profile', icon: UserRound },
]

const syncNetworkState = () => { online.value = navigator.onLine }
onMounted(() => {
  void store.initialize()
  window.addEventListener('online', syncNetworkState)
  window.addEventListener('offline', syncNetworkState)
  syncClock()
  clockTimer = window.setInterval(syncClock, 30_000)
  const scroller = viewport.value
  if (scroller) {
    scroller.addEventListener('scroll', onScroll, { passive: true })
    scroller.addEventListener('touchstart', onTouchStart, { passive: true })
    scroller.addEventListener('touchmove', onTouchMove, { passive: false })
    scroller.addEventListener('touchend', onTouchEnd)
    scroller.addEventListener('touchcancel', onTouchEnd)
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('online', syncNetworkState)
  window.removeEventListener('offline', syncNetworkState)
  window.clearInterval(clockTimer)
  window.clearTimeout(shellToastTimer)
  removeNavGuard()
  const scroller = viewport.value
  if (scroller) {
    scroller.removeEventListener('scroll', onScroll)
    scroller.removeEventListener('touchstart', onTouchStart)
    scroller.removeEventListener('touchmove', onTouchMove)
    scroller.removeEventListener('touchend', onTouchEnd)
    scroller.removeEventListener('touchcancel', onTouchEnd)
  }
})
</script>

<template>
  <div class="mobile-stage">
    <div class="mobile-frame">
      <span class="mobile-frame__keys" aria-hidden="true"><i class="is-action" /><i class="is-volup" /><i class="is-voldown" /><i class="is-power" /><i class="is-camera" /></span>
      <section class="mobile-device" aria-label="洗掌柜移动履约端">
      <div class="mobile-statusbar" :class="{ 'is-light': statusLight }" aria-hidden="true">
        <span>{{ clock }}</span>
        <svg viewBox="0 0 58 12" width="58" height="12">
          <g fill="currentColor" stroke="none">
            <rect x="0" y="7.5" width="2.4" height="4" rx=".7" /><rect x="4" y="5.5" width="2.4" height="6" rx=".7" />
            <rect x="8" y="3.5" width="2.4" height="8" rx=".7" /><rect x="12" y="1.5" width="2.4" height="10" rx=".7" />
            <circle cx="24.5" cy="10.4" r="1.4" />
          </g>
          <g fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round">
            <path d="M21.4 7.5a4.4 4.4 0 0 1 6.2 0" /><path d="M19 4.6a8.4 8.4 0 0 1 11 0" />
          </g>
          <g>
            <rect x="35" y="1.6" width="19" height="8.8" rx="2.6" fill="none" stroke="currentColor" stroke-opacity=".45" stroke-width="1.1" />
            <rect x="37" y="3.6" width="12.5" height="4.8" rx="1.3" fill="currentColor" />
            <rect x="55.2" y="4.2" width="1.8" height="3.6" rx=".9" fill="currentColor" fill-opacity=".5" />
          </g>
        </svg>
      </div>
      <div v-if="!online" class="mobile-offline" role="status">当前离线，已进入只读与待同步模式</div>
      <div v-if="!store.hydrated" class="mobile-app-loading" role="status"><i /><strong>正在同步门店任务</strong><small>订单、设备与回件状态</small></div>
      <div class="mobile-ptr" :class="{ 'is-visible': pullDistance > 0, 'is-refreshing': refreshing }" :style="{ '--pull': `${pullDistance}px` }" aria-hidden="true"><i /></div>
      <div ref="viewport" class="mobile-device__viewport" :class="{ 'has-bottom-nav': !hideNav, 'is-scrolled': scrolled }">
        <RouterView v-slot="{ Component }">
          <Transition :name="transitionName" mode="out-in" @enter="restoreScroll">
            <component :is="Component" />
          </Transition>
        </RouterView>
      </div>
      <Transition name="mobile-toast"><p v-if="shellToast" class="mobile-toast" role="status">{{ shellToast }}</p></Transition>

      <nav v-if="!hideNav" class="mobile-bottom-nav" aria-label="主要导航">
        <RouterLink
          v-for="item in navItems"
          :key="item.tab"
          :to="item.path"
          :class="{ 'is-active': activeTab === item.tab, 'is-primary': item.primary }"
          :aria-current="activeTab === item.tab ? 'page' : undefined"
        >
          <span><component :is="item.icon" :size="item.primary ? 25 : 22" :stroke-width="item.primary ? 2.4 : 2" /></span>
          <small>{{ item.label }}</small>
        </RouterLink>
      </nav>
      </section>
    </div>
  </div>
</template>
